import { createMiddleware, createServerOnlyFn } from "@tanstack/react-start";

import type { AuthUser } from "~/lib/auth-middleware";
import { ADMIN_ACCESS_REQUIRED_MESSAGE } from "~/lib/auth/auth-error";
import { setSentryUser } from "~/lib/server/sentry-context";
import { hasPermission, type Permission } from "~/lib/shared/permissions";

// Server-fn `.middleware([...])` lists keep this module in the client bundle.
// createServerOnlyFn severs the edge to the database lookup in the client
// build.
const importPermissions = createServerOnlyFn(
  () => import("~/lib/server/permissions"),
);

/**
 * Lets the call through only when the signed-in person's roles grant
 * `permission` (`write` includes `read`). Checks before loading the user, so a
 * refused call costs one database query.
 *
 * Aliases: admin auth, admin middleware, permission check, role check
 */
export function permissionMiddleware(permission: Permission) {
  return createMiddleware({ type: "function" }).server<{ user: AuthUser }>(
    async ({ next }) => {
      const { auth } = await import("@clerk/tanstack-react-start/server");
      const authState = await auth();
      if (!authState?.userId) {
        throw new Error("Authentication required");
      }

      const { getPermissionsForClerkUser } = await importPermissions();
      const permissions = await getPermissionsForClerkUser(authState.userId);
      if (!hasPermission(permissions, permission)) {
        throw new Error(ADMIN_ACCESS_REQUIRED_MESSAGE);
      }

      const { getUser } = await import("~/lib/auth-middleware");
      const user = await getUser(authState.userId);
      setSentryUser({ id: user.id, email: user.email, type: "admin" });

      return next({ context: { user } });
    },
  );
}
